import React, { useEffect, useState } from "react";
import { X, AlertTriangle, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import ProductSwapPicker from "@/components/ProductSwapPicker";

/**
 * Edit a cagnotte (commercial-only). Allows changing group name, representative
 * contact, participants count, and the underlying product. Backend handles
 * the "auto-complete on cheaper product" logic + change_log.
 */
export default function EditReservationModal({ reservation, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!reservation) {
      setForm(null);
      return;
    }
    setForm({
      group_name: reservation.group_name || "",
      representative_name: reservation.representative_name || reservation.creator_name || "",
      representative_phone: reservation.representative_phone || reservation.creator_phone || "",
      participants_count: reservation.participants_count || 2,
      product_id: reservation.product_id || "",
      reason: "",
    });
    api.get("/admin/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, [reservation]);

  if (!reservation || !form) return null;

  const selectedProduct = products.find((p) => p.id === form.product_id);
  const productChanged = form.product_id !== reservation.product_id;
  const newPrice = selectedProduct?.final_price ?? selectedProduct?.price ?? 0;
  const oldPrice = reservation.price_snapshot || 0;
  const paidCount = reservation.paid_participants_count || 0;
  const collected = paidCount * (oldPrice / Math.max(1, reservation.participants_count || 1));
  const willAutoComplete = productChanged && newPrice > 0 && collected >= newPrice;
  const willHaveOverflow = willAutoComplete ? collected - newPrice : 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (productChanged && !form.reason.trim()) {
      setError("Indiquez la raison du changement de cadeau.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {};
      if (form.group_name !== (reservation.group_name || "")) payload.group_name = form.group_name;
      const repName = reservation.representative_name || reservation.creator_name || "";
      const repPhone = reservation.representative_phone || reservation.creator_phone || "";
      if (form.representative_name !== repName) payload.representative_name = form.representative_name;
      if (form.representative_phone !== repPhone) payload.representative_phone = form.representative_phone;
      if (form.reason) payload.reason = form.reason;
      if (Number(form.participants_count) !== reservation.participants_count) {
        payload.participants_count = Number(form.participants_count);
      }
      if (form.product_id !== reservation.product_id) {
        payload.product_id = form.product_id;
      }
      const { data } = await api.patch(`/admin/reservations/${reservation.id}`, payload);
      onSaved?.(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec de la modification.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      data-testid="edit-reservation-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[92vh] overflow-y-auto sm:m-4 rounded-t-2xl">
        <div className="sticky top-0 bg-white border-b border-[#F2E2EC] px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#D81B8C]">
              Modifier la cagnotte
            </p>
            <h2 className="font-display text-xl font-black text-[#2A0E3C]">
              {reservation.group_name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="edit-reservation-close"
            className="w-9 h-9 rounded-full bg-[#FCE7F3] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              Nom du groupe
            </Label>
            <Input
              value={form.group_name}
              onChange={(e) => setForm({ ...form, group_name: e.target.value })}
              data-testid="edit-reservation-group"
              className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
                Représentant
              </Label>
              <Input
                value={form.representative_name}
                onChange={(e) => setForm({ ...form, representative_name: e.target.value })}
                data-testid="edit-reservation-rep-name"
                className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
                Téléphone
              </Label>
              <Input
                value={form.representative_phone}
                onChange={(e) => setForm({ ...form, representative_phone: e.target.value })}
                data-testid="edit-reservation-rep-phone"
                className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              Nombre de participants
            </Label>
            <Input
              type="number"
              min={Math.max(2, paidCount)}
              value={form.participants_count}
              onChange={(e) => setForm({ ...form, participants_count: e.target.value })}
              data-testid="edit-reservation-participants"
              className="mt-1 h-11 rounded-xl border-[#F2E2EC]"
            />
            <p className="text-[11px] text-[#5C4664] mt-1">
              {paidCount} déjà payé(s). Le minimum est {Math.max(2, paidCount)}.
            </p>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              Cadeau choisi
            </Label>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="edit-reservation-product-btn"
              className="mt-1 w-full h-auto min-h-11 rounded-xl border border-[#F2E2EC] bg-white px-3 py-2 text-left text-sm hover:border-[#D81B8C]/50 transition-colors flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-3 min-w-0 flex-1">
                {selectedProduct?.image_url && (
                  <span className="w-10 h-10 rounded-lg bg-[#FCE7F3] overflow-hidden shrink-0">
                    <img
                      src={selectedProduct.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block font-display font-black text-[#2A0E3C] truncate">
                    {selectedProduct?.name || "Sélectionner un cadeau…"}
                  </span>
                  {selectedProduct && (
                    <span className="block text-xs text-[#5C4664]">
                      ${(selectedProduct.final_price ?? selectedProduct.price ?? 0).toFixed(0)}
                      {selectedProduct.is_special ? " · Spécial" : ""}
                      {selectedProduct.archived ? " · archivé" : ""}
                    </span>
                  )}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-[#5C4664] shrink-0" />
            </button>
          </div>

          {productChanged && (
            <>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
                  Raison du changement <span className="text-[#D81B8C]">*</span>
                </Label>
                <Textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="edit-reservation-reason"
                  placeholder="Ex : produit indisponible, demande du groupe…"
                  rows={2}
                  className="mt-1 rounded-xl border-[#F2E2EC]"
                />
              </div>
              {willAutoComplete && (
                <div
                  data-testid="edit-reservation-autocomplete-warn"
                  className="flex items-start gap-2 bg-[#E8F6E5] border border-[#3FA535]/30 rounded-xl px-3 py-2.5 text-xs text-[#206F18]"
                >
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>Clôture automatique :</strong> le nouveau cadeau (${newPrice.toFixed(0)})
                    est déjà couvert par les ${collected.toFixed(0)} collectés.
                    {willHaveOverflow > 0 && (
                      <> Excédent de <strong>${willHaveOverflow.toFixed(2)}</strong> tracé dans l&apos;historique.</>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-[#FCE7F3] border border-[#D81B8C]/30 rounded-xl px-3 py-2 text-xs text-[#A60F6E]">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p data-testid="edit-reservation-error">{error}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="edit-reservation-cancel"
              className="flex-1 rounded-full border-[#F2E2EC]"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="edit-reservation-save"
              className="flex-1 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white"
            >
              {submitting ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </form>
      </div>
      <ProductSwapPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(p) => setForm({ ...form, product_id: p.id })}
        currentProductId={form.product_id}
        currentCategory={selectedProduct?.category}
        currentPrice={selectedProduct?.final_price ?? selectedProduct?.price ?? 0}
      />
    </div>
  );
}
